import React, { useState, useEffect } from 'react';
import { createBacktest, runBacktest, optimizeWalkForward, updateStrategy } from '../../api';
import { StatCard } from './utils';
import { useBackgroundRuns } from '../../context/BackgroundRunsContext';

function applyOptimizationParamsToGraph(baseGraph, bestParams) {
  const graph = JSON.parse(JSON.stringify(baseGraph || { nodes: [], edges: [] }));
  const byId = {};
  (graph.nodes || []).forEach((n) => { byId[String(n.id)] = n; });

  Object.entries(bestParams || {}).forEach(([key, rawVal]) => {
    if (!key || !key.includes(':')) return;
    const [nodeId, pathStr] = key.split(':', 2);
    const node = byId[nodeId];
    if (!node) return;
    const path = (pathStr || '').split('.').filter(Boolean);
    if (path.length === 0 || path[0] !== 'data') return;

    let ref = node;
    for (let i = 0; i < path.length - 1; i += 1) {
      const p = path[i];
      if (!ref[p] || typeof ref[p] !== 'object') ref[p] = {};
      ref = ref[p];
    }
    const leaf = path[path.length - 1];
    let value = Number(rawVal);
    if (!Number.isFinite(value)) return;
    if (leaf.toLowerCase().includes('pct') || leaf.toLowerCase().includes('percent')) {
      value = Math.max(0, Math.min(100, value));
    }
    ref[leaf] = value;
  });

  (graph.nodes || []).forEach((n) => {
    if (n?.type !== 'action') return;
    const raw = n?.data?.size_pct;
    const val = Number(raw);
    if (Number.isFinite(val)) n.data.size_pct = Math.max(0, Math.min(100, val));
  });

  return graph;
}

export default function OptimizationPanel({ backtest, strategyId, strategyForEdit, onStrategyUpdated, onRerunResult, folds }) {
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [optimizeBusy, setOptimizeBusy] = useState(false);
  const [optimizeInfo, setOptimizeInfo] = useState('');
  const [optimizeError, setOptimizeError] = useState('');
  const [optimizeObjective, setOptimizeObjective] = useState('composite');
  const [optimizeTrials, setOptimizeTrials] = useState(30);
  const [optimizeWeights, setOptimizeWeights] = useState({ return: 1.0, sharpe: 0.6, drawdown: 0.8, stability: 0.2 });
  const [optimizationData, setOptimizationData] = useState(backtest?.results?.wfa_optimization || null);
  const [optimizeApplyBusy, setOptimizeApplyBusy] = useState(false);
  const [optimizeSaveBusy, setOptimizeSaveBusy] = useState(false);
  const [trialRunsOpen, setTrialRunsOpen] = useState(false);
  const { startRun } = useBackgroundRuns();

  useEffect(() => {
    setOptimizationData(backtest?.results?.wfa_optimization || null);
    setOptimizeInfo('');
    setOptimizeError('');
  }, [backtest?.id, backtest?.results?.wfa_optimization]);

  const handleRunOptimization = async () => {
    if (!backtest?.id) return;
    const payloadFolds = (folds || [])
      .filter((f) => f?.test_start && f?.test_end)
      .map((f, idx) => ({
        label: String(f.label || `fold_${idx + 1}`),
        test_start: f.test_start,
        test_end: f.test_end,
      }));

    if (payloadFolds.length === 0) {
      setOptimizeError('Add at least one valid OOS fold before optimization.');
      return;
    }

    setOptimizeBusy(true);
    setOptimizeInfo('');
    setOptimizeError('');
    try {
      const resp = await optimizeWalkForward(backtest.id, {
        scheme: 'external_oos',
        folds: payloadFolds,
        objective: optimizeObjective,
        trials: Math.max(1, Number(optimizeTrials) || 30),
        weights: {
          return: Number(optimizeWeights.return) || 1.0,
          sharpe: Number(optimizeWeights.sharpe) || 0.6,
          drawdown: Number(optimizeWeights.drawdown) || 0.8,
          stability: Number(optimizeWeights.stability) || 0.2,
        },
      });
      if (resp?.data?.queued) {
        setOptimizeInfo(resp.data.detail || 'Optimization queued in the background.');
        startRun({
          backtestId: backtest.id,
          kind: 'optimization',
          onComplete: (data) => {
            setOptimizationData(data?.results?.wfa_optimization || null);
            setOptimizeInfo('WFA optimization completed.');
          },
        });
      } else {
        setOptimizeInfo('Optimization request sent.');
      }
    } catch (err) {
      setOptimizeError(err.response?.data?.detail || 'Failed to start optimization.');
    } finally {
      setOptimizeBusy(false);
    }
  };

  const handleApplyOptimizationToResults = async () => {
    if (!optimizationData?.best_params) {
      setOptimizeError('No optimization result is available to apply.');
      return;
    }
    setOptimizeApplyBusy(true);
    setOptimizeError('');
    setOptimizeInfo('');
    try {
      const createResp = await createBacktest({
        strategy: strategyId,
        asset_type: backtest.asset_type,
        symbol: backtest.symbol,
        start_date: backtest.start_date,
        end_date: backtest.end_date,
        initial_cash: backtest.initial_cash,
        slippage_perc: backtest.slippage_perc,
        commission_perc: backtest.commission_perc,
        fill_on_close: backtest.fill_on_close,
        fx_interval: backtest.fx_interval,
      });

      const runResp = await runBacktest(createResp.data.id, {
        param_overrides: optimizationData.best_params,
      });

      if (runResp?.data?.queued) {
        setOptimizeInfo(runResp.data.detail || 'Optimized preview was queued in the background.');
      } else {
        if (onRerunResult) onRerunResult(runResp.data);
        setOptimizeInfo('Applied optimized params to a preview backtest result.');
      }
    } catch (err) {
      setOptimizeError(err.response?.data?.detail || 'Failed to apply optimized params to results.');
    } finally {
      setOptimizeApplyBusy(false);
    }
  };

  const handleSaveOptimizationToStrategy = async () => {
    if (!optimizationData?.best_params) {
      setOptimizeError('No optimization result is available to save.');
      return;
    }
    if (!strategyForEdit?.id) {
      setOptimizeError('Strategy is still loading. Please try again in a moment.');
      return;
    }

    setOptimizeSaveBusy(true);
    setOptimizeError('');
    setOptimizeInfo('');
    try {
      const updatedGraph = applyOptimizationParamsToGraph(strategyForEdit.graph, optimizationData.best_params);
      const resp = await updateStrategy(strategyForEdit.id, {
        name: strategyForEdit.name,
        description: strategyForEdit.description || '',
        graph: updatedGraph,
      });
      onStrategyUpdated(resp.data);
      setOptimizeInfo('Saved optimized parameters to your strategy.');
    } catch (err) {
      setOptimizeError(err.response?.data?.detail || 'Failed to save optimized params to strategy.');
    } finally {
      setOptimizeSaveBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <button
        onClick={() => setOptimizeOpen((v) => !v)}
        style={{
          background: 'var(--surface)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '7px 12px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {optimizeOpen ? 'Hide WFA Optimization' : 'WFA Optimization'}
      </button>

      {optimizeOpen && (
        <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            Optimize numeric strategy parameters with Optuna against your OOS folds.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Objective</span>
              <select
                value={optimizeObjective}
                onChange={(e) => setOptimizeObjective(e.target.value)}
                style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 8px' }}
              >
                <option value="composite">Composite Robustness</option>
                <option value="mean_return">Mean OOS Return</option>
                <option value="mean_sharpe">Mean OOS Sharpe</option>
                <option value="min_drawdown">Min Worst Drawdown</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Trials</span>
              <input
                type="number"
                min={1}
                max={500}
                value={optimizeTrials}
                onChange={(e) => setOptimizeTrials(e.target.value)}
                style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 8px' }}
              />
            </label>
          </div>

          {optimizeObjective === 'composite' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
              {['return', 'sharpe', 'drawdown', 'stability'].map((k) => (
                <label key={k} style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{`Weight: ${k}`}</span>
                  <input
                    type="number"
                    step="0.1"
                    value={optimizeWeights[k]}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOptimizeWeights((prev) => ({ ...prev, [k]: v }));
                    }}
                    style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 8px' }}
                  />
                </label>
              ))}
            </div>
          )}

          <div>
            <button
              onClick={handleRunOptimization}
              disabled={optimizeBusy}
              style={{
                background: optimizeBusy ? 'var(--subtle)' : 'var(--green)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 8,
                padding: '8px 12px',
                fontWeight: 700,
                cursor: optimizeBusy ? 'wait' : 'pointer',
              }}
            >
              {optimizeBusy ? 'Queueing…' : 'Run Optimization'}
            </button>
          </div>

          {optimizeError && <div style={{ color: 'var(--red)', fontSize: 12 }}>{optimizeError}</div>}
          {optimizeInfo && <div style={{ color: 'var(--teal)', fontSize: 12 }}>{optimizeInfo}</div>}

          {optimizationData && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Latest Optimization Result</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
                <StatCard label="Objective" value={optimizationData.objective || '—'} />
                <StatCard label="Trials" value={optimizationData.trials ?? '—'} />
                <StatCard label="Best Score" value={optimizationData.best_score != null ? Number(optimizationData.best_score).toFixed(4) : '—'} />
                <StatCard label="Mean OOS Return" value={optimizationData.summary?.mean_return_pct != null ? `${optimizationData.summary.mean_return_pct}%` : '—'} />
              </div>
              {optimizationData.best_params && (
                <div style={{ marginTop: 10, maxHeight: 180, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Best Parameters</div>
                  {Object.entries(optimizationData.best_params).map(([k, v]) => (
                    <div key={k} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text)', marginBottom: 3 }}>
                      {k}: {String(v)}
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(optimizationData.folds) && optimizationData.folds.length > 0 && (
                <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Date Ranges Used (Train/Test)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
                    {optimizationData.folds.map((f, idx) => (
                      <div
                        key={`opt-fold-${idx}`}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          background: 'var(--surface)',
                          padding: '8px 10px',
                        }}
                      >
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {f.label || `fold_${idx + 1}`}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Train</div>
                        <div style={{ fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
                          {f.train_start} -> {f.train_end}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Test</div>
                        <div style={{ fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                          {f.test_start} -> {f.test_end}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(optimizationData.trial_runs) && optimizationData.trial_runs.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button
                    onClick={() => setTrialRunsOpen((v) => !v)}
                    style={{
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '7px 10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {trialRunsOpen
                      ? `Hide Trial Runs (${optimizationData.trial_runs.length})`
                      : `Show Trial Runs (${optimizationData.trial_runs.length})`}
                  </button>

                  {trialRunsOpen && (
                    <div style={{ marginTop: 8, overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Trial</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Score</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Mean Return</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Mean Sharpe</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Worst DD</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Win Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {optimizationData.trial_runs.map((t) => (
                            <tr key={`trial-${t.number}`} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '6px 8px', color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{t.number}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                                {t.score != null ? Number(t.score).toFixed(4) : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                                {t.summary?.mean_return_pct != null ? `${t.summary.mean_return_pct}%` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                                {t.summary?.mean_sharpe_ratio != null ? Number(t.summary.mean_sharpe_ratio).toFixed(4) : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                                {t.summary?.worst_max_drawdown_pct != null ? `${t.summary.worst_max_drawdown_pct}%` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                                {t.summary?.win_rate_pct != null ? `${t.summary.win_rate_pct}%` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={handleApplyOptimizationToResults}
                  disabled={optimizeApplyBusy || optimizeSaveBusy || !optimizationData?.best_params}
                  style={{
                    background: optimizeApplyBusy ? 'var(--subtle)' : 'var(--teal)',
                    color: 'var(--bg)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontWeight: 700,
                    cursor: optimizeApplyBusy ? 'wait' : 'pointer',
                  }}
                >
                  {optimizeApplyBusy ? 'Applying…' : 'Apply to Results'}
                </button>
                <button
                  onClick={handleSaveOptimizationToStrategy}
                  disabled={optimizeApplyBusy || optimizeSaveBusy || !optimizationData?.best_params}
                  style={{
                    background: optimizeSaveBusy ? 'var(--subtle)' : 'var(--surface)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontWeight: 700,
                    cursor: optimizeSaveBusy ? 'wait' : 'pointer',
                  }}
                >
                  {optimizeSaveBusy ? 'Saving…' : 'Save to Strategy'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
